import { deepMerge } from '../util/data';
import type { Token } from '../core/token';

// eslint-disable-next-line max-lines-per-function
export default (overrideToken?: Partial<Token>): Partial<Token> =>
  deepMerge(
    {
      textStyle: {
        fontFamily: 'Microsoft YaHei',
        fontWeight: 'normal',
        fontSize: 12,
        lineHeight: 12
      },
      dvStandardChart: {
        baseOption: {
          grid: {
            containLabel: true,
            top: 10,
            bottom: 5,
            left: 0,
            right: 5
          },
          tooltip: {
            backgroundColor: '#fff',
            extraCssText:
              'border-radius: 4px; padding: 8px; border-color: transparent; box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.2);'
          },
          dataZoom: {
            show: false
          },
          xAxis: {
            axisLine: {
              lineStyle: {
                color: '#858585'
              }
            },
            axisPointer: {
              shadowStyle: {
                opacity: 0.2
              }
            },
            nameTextStyle: {
              dvZ: 5
            },
            splitLine: {
              dvZ: 0
            }
          },
          yAxis: {
            axisLine: {
              lineStyle: {
                color: '#858585'
              }
            },
            nameTextStyle: {
              dvZ: 5
            },
            splitLine: {
              dvZ: 0
            }
          },
          xAxis3D: {
            axisLine: {
              lineStyle: {
                color: '#858585'
              }
            }
          },
          yAxis3D: {
            axisLine: {
              lineStyle: {
                color: '#858585'
              }
            }
          },
          zAxis3D: {
            axisLine: {
              lineStyle: {
                color: '#858585'
              }
            }
          },
          grid3D: {
            light: {
              main: {
                intensity: 1.2,
                shadow: true
              },
              ambient: {
                intensity: 0.3
              }
            }
          },
          dvInsight: [
            {
              textSummary: {
                circleStyle: {
                  type: 'circle',
                  shape: {
                    cx: 0,
                    cy: 0,
                    r: 4
                  },
                  style: {
                    fill: 'black',
                    stroke: 'transparent'
                  },
                  z2: 9999
                },
                lineStyle: {
                  type: 'line',
                  style: {
                    stroke: 'black',
                    lineWidth: 1,
                    lineHeight: 24
                  },
                  z2: 9999
                },
                rectStyle: {
                  type: 'rect',
                  shape: {
                    r: 2
                  },
                  style: {
                    fill: 'black',
                    opacity: 0.8
                  },
                  z: 9999,
                  z2: 9999
                },
                textStyle: {
                  type: 'text',
                  style: {
                    x: 0,
                    y: 0,
                    fill: 'white',
                    padding: [4, 6]
                  },
                  z: 9999,
                  z2: 9999
                }
              },
              blurSeries: {
                itemStyle: {
                  opacity: 0.2,
                  colorAlpha: 0.2
                },
                lineStyle: {
                  opacity: 0.2
                },
                areaStyle: {
                  opacity: 0.2
                }
              }
            }
          ],
          series: [
            {
              type: 'bar3D',
              shading: 'lambert',
              label: {
                fontSize: 16,
                borderWidth: 1
              }
            },
            {
              type: 'gauge',
              axisTick: {
                show: false,
                length: 8,
                lineStyle: {
                  color: '#fff',
                  width: 2
                }
              },
              splitLine: {
                show: false,
                length: 30,
                lineStyle: {
                  color: '#fff',
                  width: 4
                }
              },
              pointer: {
                show: false,
                itemStyle: {
                  color: 'auto'
                }
              },
              title: {
                show: false,
                fontSize: 12,
                lineHeight: 12
              },
              detail: {
                show: false,
                height: 12,
                fontWeight: 'normal',
                fontSize: 12,
                lineHeight: 12,
                padding: [2, 8, 2, 8],
                color: 'inherit',
                borderColor: 'inherit',
                borderWidth: 1
              }
            },
            {
              type: 'pie',
              emphasis: {
                focus: 'self',
                blurScope: 'global',
                itemStyle: {
                  shadowBlur: 10,
                  shadowOffsetX: 0,
                  shadowColor: 'rgba(0, 0, 0, 0)'
                }
              },
              blur: {
                itemStyle: {
                  opacity: 0.2
                }
              }
            }
          ]
        }
      },
      dvTimeline: {
        theme: 'pc',
        domCSSText: 'padding: 0; box-shadow: none; border-color: transparent;'
      },

      // * --- echarts theme  @see https://echarts.apache.org/zh/theme-builder.html
      backgroundColor: 'rgba(0, 0, 0, 0)',

      // * --- UED Token -----------------------------------------------------------------------------------
      // /** 色板 */
      '--color-visualization': [
        '#3366FF',
        '#FF4019',
        '#ff9500',
        '#62b312',
        '#14ccbd',
        '#199fff',
        '#4433ff',
        '#ff33aa',
        '#ff33aa',
        '#858585'
      ],
      '--color-text-inverse-primary': 'rgba(255,255,255,0.84)',
      '--color-text-inverse-secondary': 'rgba(255,255,255,0.6)',
      '--color-text-inverse-tertiary': 'rgba(255,255,255,0.4)',
      '--color-text-inverse-quaternary': 'rgba(255,255,255,0.24)',

      '--font-weight-regular': 400,
      '--font-weight-medium': 500,
      '--font-weight-bold': 600,

      '--font-size-super-large': 24,
      '--font-size-extra-large': 20,
      '--font-size-large': 18,
      '--font-size-base': 16,
      '--font-size-small': 13,
      '--font-size-extra-small': 12,
      '--font-size-super-small': 11,
      '--font-size-xxs': 10,
      '--font-size-xxxs': 9,

      '--line-height-super-large': 30,
      '--line-height-extra-large': 24,
      '--line-height-large': 22,
      '--line-height-base': 20,
      '--line-height-medium': 18,
      '--line-height-small': 16,
      '--line-height-extra-small': 14,
      '--line-height-super-small': 12,
      '--line-height-xxs': 10
    },
    overrideToken,
    { override: true }
  ) as Token;
